import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_PRIMARY } from "@/lib/brand";
import { track } from "@/lib/analytics";

const LOGO = LOGO_PRIMARY;

const LEFT_LINKS = [
 { to: "/", label: "Home", end: true },
 { to: "/about", label: "About" },
 { to: "/services", label: "Services" },
];
const RIGHT_LINKS = [
 { to: "/projects", label: "Projects" },
 { to: "/contact", label: "Contact" },
];

export default function Navbar() {
 const [scrolled, setScrolled] = useState(false);
 const [open, setOpen] = useState(false);
 const location = useLocation();

 useEffect(() => {
 const onScroll = () => setScrolled(window.scrollY > 20);
 onScroll();
 window.addEventListener("scroll", onScroll, { passive: true });
 return () => window.removeEventListener("scroll", onScroll);
 }, []);

 useEffect(() => {
 setOpen(false);
 }, [location.pathname]);

 useEffect(() => {
 document.body.style.overflow = open ? "hidden" : "";
 return () => {
 document.body.style.overflow = "";
 };
 }, [open]);

 return (
 <>
 {/* Top announcement strip, compact */}
 <div
 data-testid="top-announcement"
 className="hidden md:flex items-center justify-between px-8 py-1.5 text-[10px] tracking-[0.32em] uppercase"
 style={{
 background: "#06050480",
 color: "var(--hh-text-dim)",
 fontFamily: "Marcellus, serif",
 }}
 >
 <span className="flex items-center gap-3">
 <span
 className="inline-block w-1.5 h-1.5 rounded-full"
 style={{ background: "var(--hh-gold)" }}
 />
 Tahlequah · Oklahoma
 </span>
 <span style={{ color: "var(--hh-text)" }}>
 Custom Homes · Remodels · Additions
 </span>
 <a
 data-testid="top-bar-phone"
 href="tel:+19183734341"
 className="flex items-center gap-2 hover:text-[var(--hh-gold-light)] transition-colors"
 style={{ color: "var(--hh-gold)" }}
 >
 <Phone size={10} strokeWidth={1.5} />
 (918) 373-4341
 </a>
 </div>

 {/* Main nav */}
 <header
 data-testid="main-navbar"
 className={`sticky top-0 z-50 transition-all duration-500 ${
 scrolled
 ? "backdrop-blur-xl border-b border-[#2A2522]/80"
 : "border-b border-transparent"
 }`}
 style={{
 background: scrolled
 ? "rgba(10, 9, 8, 0.88)"
 : "rgba(10, 9, 8, 0.30)",
 }}
 >
 <nav className="grid grid-cols-3 items-center px-6 md:px-10 h-24 md:h-32 max-w-[1480px] mx-auto">
 {/* Left links (desktop) */}
 <div className="hidden md:flex items-center gap-10 text-[11px] font-nav">
 {LEFT_LINKS.map((l) => (
 <NavLink
 key={l.to}
 to={l.to}
 end={l.end}
 data-testid={`nav-link-${l.label.toLowerCase()}`}
 className="nav-link"
 >
 {l.label}
 </NavLink>
 ))}
 </div>

 {/* Mobile menu trigger */}
 <div className="flex md:hidden">
 <button
 data-testid="mobile-menu-toggle"
 onClick={() => setOpen((v) => !v)}
 aria-label="Toggle menu"
 className="text-[var(--hh-text)] hover:text-[var(--hh-gold)] transition-colors"
 >
 {open ? (
 <X strokeWidth={1.2} size={26} />
 ) : (
 <Menu strokeWidth={1.2} size={26} />
 )}
 </button>
 </div>

 {/* Center logo */}
 <Link
 to="/"
 data-testid="brand-logo-link"
 className="flex items-center justify-center group"
 >
 <img
 src={LOGO}
 alt="Harris Homes LLC"
 width={600}
 height={275}
 className="h-16 md:h-20 w-auto transition-transform duration-700 group-hover:scale-[1.03]"
 style={{
 filter: "drop-shadow(0 0 14px rgba(197,160,89,0.12))",
 }}
 />
 </Link>

 {/* Right links (desktop) */}
 <div className="hidden md:flex items-center justify-end gap-10 text-[11px] font-nav">
 {RIGHT_LINKS.map((l) => (
 <NavLink
 key={l.to}
 to={l.to}
 data-testid={`nav-link-${l.label.toLowerCase()}`}
 className="nav-link"
 >
 {l.label}
 </NavLink>
 ))}
 </div>

 {/* Mobile CTA placeholder right column */}
 <div className="flex md:hidden justify-end">
 <a
 href="tel:+19183734341"
 data-testid="mobile-phone-link"
 onClick={() => track.phoneClick("navbar-mobile")}
 className="text-[var(--hh-gold)]"
 aria-label="Call Harris Homes"
 >
 <Phone size={20} strokeWidth={1.4} />
 </a>
 </div>
 </nav>
 </header>

 {/* Mobile drawer */}
 <div
 data-testid="mobile-menu-drawer"
 className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
 open
 ? "opacity-100 pointer-events-auto"
 : "opacity-0 pointer-events-none"
 }`}
 style={{ background: "rgba(10,9,8,0.97)" }}
 >
 <div className="h-20" />
 <div className="px-8 pt-12 flex flex-col gap-8">
 {[...LEFT_LINKS, ...RIGHT_LINKS].map((l, i) => (
 <NavLink
 key={l.to}
 to={l.to}
 end={l.end}
 data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
 className="font-nav text-2xl"
 style={{
 color: "var(--hh-text)",
 animationDelay: `${i * 60}ms`,
 }}
 >
 <span style={{ color: "var(--hh-gold)" }}>
 0{i + 1}.
 </span>{" "}
 {l.label}
 </NavLink>
 ))}
 <div className="gold-rule mt-4" />
 <div className="flex flex-col gap-3 mt-2">
 <a
 href="tel:+19183734341"
 data-testid="mobile-drawer-phone"
 onClick={() => track.phoneClick("navbar-drawer")}
 className="font-display text-3xl"
 style={{ color: "var(--hh-gold)" }}
 >
 (918) 373-4341
 </a>
 <a
 href="mailto:4harrishomes@gmail.com"
 data-testid="mobile-drawer-email"
 onClick={() => track.emailClick("navbar-drawer")}
 className="font-body text-sm"
 style={{ color: "var(--hh-text-dim)" }}
 >
 4harrishomes@gmail.com
 </a>
 </div>
 </div>
 </div>
 </>
 );
}
